// Shared vehicle-spec presentation for BOTH the search card (VehicleCard) and
// the booking hero (CarShowcase), so the two never drift apart. SpecGrid renders
// the key facts (seats, doors, transmission, fuel, luggage, location) as a
// FRAMELESS 3-column / 2-row grid of icon + value pairs, using Tabler icons that
// adapt to the value (a manual vs automatic gearbox, a fuel pump vs a leaf for
// hybrid). `size="lg"` is the bigger booking-page variant. The icon-only feature
// badges live in the sibling `FeatureBadges` client component.
// Custom MULTI-COLOUR icons (user-supplied artwork reworked onto our palette in
// SpecIcons.js — layered paths, each part its own --sf-* colour). Fuel is
// contextual: dedicated petrol / hybrid / electric-station glyphs.
import {
    SeatsIcon,
    CarDoorIcon,
    TransmissionIcon,
    PetrolIcon,
    HybridIcon,
    ElectricIcon,
    LuggageIcon,
    LocationIcon,
} from "./SpecIcons";
import styles from "./car-specs.module.css";

const fuelIcon = (v) => {
    const s = String(v || "").toLowerCase();
    if (/electric|ev\b/.test(s)) return ElectricIcon;
    if (/hybrid/.test(s)) return HybridIcon;
    return PetrolIcon; // petrol, diesel, gas, …
};

// Order defines the grid reading order (left→right, top→bottom). Exactly six so
// a complete car fills a clean 3×2; a missing value drops its item and the rest
// reflow. Each cell reads "Passengers: 5" — a muted caption STACKED over the
// value (inline "Transmission: Automatic" can't fit the ~105px card columns).
const SPECS = [
    { key: "passengers", pick: () => SeatsIcon, get: (c) => c.passengers && String(c.passengers), caption: "Passengers" },
    { key: "doors", pick: () => CarDoorIcon, get: (c) => c.doors && String(c.doors), caption: "Doors" },
    { key: "transmission", pick: () => TransmissionIcon, get: (c) => c.transmission, caption: "Transmission" },
    { key: "fuel", pick: (c) => fuelIcon(c.engine_type), get: (c) => c.engine_type, caption: "Fuel" },
    { key: "luggage", pick: () => LuggageIcon, get: (c) => c.luggage && String(c.luggage), caption: "Luggage" },
    { key: "location", pick: () => LocationIcon, get: (c) => c.location, caption: "Location" },
];

export function SpecGrid({ car = {}, size }) {
    const lg = size === "lg";
    const items = SPECS
        .map((s) => ({ key: s.key, Icon: s.pick(car), label: s.get(car), caption: s.caption }))
        .filter((s) => s.label);
    if (!items.length) return null;
    return (
        <ul className={`${styles.grid} ${lg ? styles.gridLg : ""}`}>
            {items.map(({ key, Icon, label, caption }) => (
                <li key={key} className={styles.tile} title={`${caption}: ${label}`}>
                    <Icon className={styles.tileIcon} />
                    <span className={styles.tileText}>
                        <span className={styles.tileCaption}>{caption}</span>
                        <span className={styles.tileLabel}>{label}</span>
                    </span>
                </li>
            ))}
        </ul>
    );
}
